/*
  foodora eShop – core runtime (no dependencies, no build step)
  Loaded with `defer` on every page, before any other fd-*.js file.

  Exposes `window.FD`:
    FD.config                 data from snippets/fd-config.liquid
    FD.t(key, vars)           translated string from FD.config.strings
    FD.money(cents)           format cents with the shop's money format
    FD.fetchJSON(url, opts)   fetch + JSON + typed errors
    FD.announce(message)      polite screen-reader announcement
    FD.cart.get / add / change / render
    FD.on / FD.emit           tiny event helpers on `document`
  Custom elements:
    <fd-qty>                  quantity stepper (optional data-pack/data-carton/data-pieces + [data-fd-qty-suffix] show "1 KT (2.500 Stk.)")
  Declarative dialog helpers (native <dialog>):
    [data-fd-open="#id"]      opens modal dialog #id
    [data-fd-close]           closes the surrounding dialog
    Clicking the dialog's backdrop closes it.

  Cart-driven re-rendering contract
    <section-root data-fd-cart-section="{{ section.id }}"> registers a section.
    Inside it, <el data-fd-render="name"> regions are swapped with the freshly
    rendered markup after every cart change.
*/
(function () {
  'use strict';

  var FD = (window.FD = window.FD || {});
  var cfg = (FD.config = window.FD_CONFIG || {});
  cfg.strings = cfg.strings || {};
  cfg.routes = cfg.routes || {
    cart: '/cart',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js',
    cartGet: '/cart.js'
  };

  /* ---------- Strings & money ---------- */
  FD.t = function (key, vars) {
    var s = cfg.strings[key];
    if (s == null) return '';
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.split('{{ ' + k + ' }}').join(vars[k]).split('{{' + k + '}}').join(vars[k]);
      });
    }
    return s;
  };

  var decodeEl = document.createElement('textarea');
  function decodeEntities(str) {
    decodeEl.innerHTML = str;
    return decodeEl.value;
  }

  function group(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) return '0';
    var fixed = (number / 100).toFixed(precision).split('.');
    var whole = fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return fixed[1] ? whole + decimal + fixed[1] : whole;
  }

  // `precision` overrides the decimals of the shop format (e.g. 3 for tiny per-piece prices).
  FD.money = function (cents, precision) {
    var format = decodeEntities(cfg.moneyFormat || '{{amount}}');
    cents = Number(cents);
    var p = precision;
    function d(def) { return p == null ? def : p; }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      switch (name) {
        case 'amount': return group(cents, d(2), ',', '.');
        case 'amount_no_decimals': return group(cents, d(0), ',', '.');
        case 'amount_with_comma_separator': return group(cents, d(2), '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(cents, d(0), '.', ',');
        case 'amount_with_apostrophe_separator': return group(cents, d(2), "'", '.');
        case 'amount_no_decimals_with_space_separator': return group(cents, d(0), ' ', ',');
        case 'amount_with_space_separator': return group(cents, d(2), ' ', ',');
        case 'amount_with_period_and_space_separator': return group(cents, d(2), ' ', '.');
        default: return group(cents, d(2), ',', '.');
      }
    });
  };

  // Price of ONE piece from a pack price (cents). Always per piece, never per 100.
  // Below 10 cent up to 4 decimals are shown (3.53 cent -> 0,0353), trailing zeros trimmed to >= 2.
  FD.unitPrice = function (packCents, packSize) {
    var perPiece = Number(packCents) / Number(packSize);
    var decimals = 2;
    if (perPiece < 10) {
      var exact = (perPiece / 100).toFixed(4);
      for (decimals = 2; decimals < 4; decimals++) {
        if ((perPiece / 100).toFixed(decimals) === Number(exact).toFixed(decimals) && Number((perPiece / 100).toFixed(decimals)) === Number(exact)) break;
      }
    }
    return FD.money(perPiece, decimals);
  };

  /* ---------- Events ---------- */
  FD.emit = function (name, detail, target) {
    (target || document).dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  };
  FD.on = function (name, handler, opts) {
    document.addEventListener(name, handler, opts);
  };

  /* ---------- Network ---------- */
  FD.fetchJSON = function (url, opts) {
    opts = opts || {};
    var headers = Object.assign({ Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, opts.headers || {});
    if (opts.body && typeof opts.body !== 'string' && !(opts.body instanceof FormData)) {
      opts.body = JSON.stringify(opts.body);
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, Object.assign({}, opts, { headers: headers, credentials: 'same-origin' })).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!res.ok || (data && data.status && data.status >= 400)) {
          var err = new Error((data && (data.description || data.message)) || FD.t('error_generic') || 'Request failed');
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  };

  /* ---------- Accessibility ---------- */
  var liveEl;
  FD.announce = function (message) {
    if (!message) return;
    if (!liveEl) {
      liveEl = document.getElementById('fd-live');
      if (!liveEl) {
        liveEl = document.createElement('div');
        liveEl.id = 'fd-live';
        liveEl.className = 'fd-sr-only';
        liveEl.setAttribute('role', 'status');
        liveEl.setAttribute('aria-live', 'polite');
        document.body.appendChild(liveEl);
      }
    }
    liveEl.textContent = '';
    window.setTimeout(function () { liveEl.textContent = message; }, 50);
  };

  FD.debounce = function (fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  };

  /* ---------- Cart ---------- */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('[data-fd-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-fd-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function withSections(payload) {
    var ids = cartSectionIds();
    if (ids.length) {
      payload.sections = ids.join(',');
      payload.sections_url = window.location.pathname;
    }
    return payload;
  }

  // Swaps every [data-fd-render] region with the freshly rendered one.
  function renderSections(sections) {
    if (!sections) return;
    var parser = new DOMParser();
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (!html) return;
      var doc = parser.parseFromString(html, 'text/html');
      var root = document.querySelector('[data-fd-cart-section="' + CSS.escape(id) + '"]');
      if (!root) return;
      doc.querySelectorAll('[data-fd-render]').forEach(function (fresh) {
        var name = fresh.getAttribute('data-fd-render');
        var live = root.querySelector('[data-fd-render="' + CSS.escape(name) + '"]') ||
          (root.matches('[data-fd-render="' + CSS.escape(name) + '"]') ? root : null);
        if (live) {
          live.innerHTML = fresh.innerHTML;
          // Mirror state attributes the server may flip (e.g. empty cart).
          ['data-empty', 'data-count'].forEach(function (attr) {
            if (fresh.hasAttribute(attr)) live.setAttribute(attr, fresh.getAttribute(attr));
            else live.removeAttribute(attr);
          });
        }
      });
      var freshRoot = doc.querySelector('[data-fd-cart-section]');
      if (freshRoot) {
        ['data-empty', 'data-count'].forEach(function (attr) {
          if (freshRoot.hasAttribute(attr)) root.setAttribute(attr, freshRoot.getAttribute(attr));
          else root.removeAttribute(attr);
        });
      }
    });
  }

  FD.cart = {
    get: function () {
      return FD.fetchJSON(cfg.routes.cartGet + '?_=' + Date.now());
    },
    add: function (items, source) {
      var payload = withSections({ items: items });
      return FD.fetchJSON(cfg.routes.cartAdd, { method: 'POST', body: payload }).then(function (res) {
        renderSections(res.sections);
        FD.emit('fd:cart:updated', { action: 'add', items: res.items, source: source || null });
        return res;
      });
    },
    // `line` is 1-based. Pass quantity 0 to remove.
    change: function (line, quantity, source) {
      var payload = withSections({ line: line, quantity: quantity });
      return FD.fetchJSON(cfg.routes.cartChange, { method: 'POST', body: payload }).then(function (cart) {
        renderSections(cart.sections);
        FD.emit('fd:cart:updated', { action: 'change', cart: cart, source: source || null });
        return cart;
      });
    },
    update: function (data, source) {
      var payload = withSections(data);
      return FD.fetchJSON(cfg.routes.cartUpdate, { method: 'POST', body: payload }).then(function (cart) {
        renderSections(cart.sections);
        FD.emit('fd:cart:updated', { action: 'update', cart: cart, source: source || null });
        return cart;
      });
    },
    render: renderSections,
    // Re-render regions from the server without changing the cart.
    refresh: function () {
      var ids = cartSectionIds();
      if (!ids.length) return Promise.resolve();
      var url = window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','));
      return FD.fetchJSON(url).then(renderSections);
    }
  };

  // Opens the cart drawer when present, otherwise goes to the cart page.
  FD.openCart = function () {
    var drawer = document.querySelector('[data-fd-cart-drawer]');
    if (drawer && typeof drawer.showModal === 'function') {
      if (!drawer.open) drawer.showModal();
      return true;
    }
    window.location.href = cfg.routes.cart;
    return false;
  };

  /* ---------- Native <dialog> helpers ---------- */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-fd-open]');
    if (opener) {
      var dlg = document.querySelector(opener.getAttribute('data-fd-open'));
      if (dlg && typeof dlg.showModal === 'function') {
        event.preventDefault();
        if (!dlg.open) dlg.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dlg.addEventListener('close', function onClose() {
          opener.setAttribute('aria-expanded', 'false');
          dlg.removeEventListener('close', onClose);
        });
      }
      return;
    }
    var closer = event.target.closest('[data-fd-close]');
    if (closer) {
      var parent = closer.closest('dialog');
      if (parent) parent.close();
      return;
    }
    // Backdrop click: the click target is the <dialog> itself.
    if (event.target.tagName === 'DIALOG' && event.target.hasAttribute('data-fd-light-dismiss')) {
      var rect = event.target.getBoundingClientRect();
      var inside = event.clientX >= rect.left && event.clientX <= rect.right &&
        event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });

  /* ---------- <fd-qty> ---------- */
  if (!customElements.get('fd-qty')) {
    class FdQty extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.input = this.querySelector('input');
        this.minus = this.querySelector('[data-fd-qty-minus]');
        this.plus = this.querySelector('[data-fd-qty-plus]');
        this.suffix = this.querySelector('[data-fd-qty-suffix]');
        if (!this.input) return;
        this.addEventListener('click', this._onClick.bind(this));
        if (this.suffix) this.suffix.addEventListener('click', function () { this.input.focus(); }.bind(this));
        this.input.addEventListener('change', this._onInput.bind(this));
        this.input.addEventListener('input', this._renderSuffix.bind(this));
        this.input.addEventListener('keydown', this._onKey.bind(this));
        this.input.addEventListener('focus', function (e) { e.target.select(); });
        this._sync();
      }
      get min() { var v = parseInt(this.getAttribute('data-min'), 10); return isNaN(v) ? 1 : v; }
      get max() { var v = parseInt(this.getAttribute('data-max'), 10); return isNaN(v) ? Infinity : v; }
      get step() { var v = parseInt(this.getAttribute('data-step'), 10); return isNaN(v) || v < 1 ? 1 : v; }
      get value() { return this._clamp(parseInt(this.input.value, 10)); }
      set value(v) { this.input.value = String(this._clamp(v)); this._sync(); }
      setMax(max) {
        if (max == null || max === '') this.removeAttribute('data-max');
        else this.setAttribute('data-max', String(max));
        this._sync();
      }
      _clamp(v) {
        if (isNaN(v)) v = this.min;
        return Math.min(Math.max(v, this.min), this.max);
      }
      _commit(v, silent) {
        var before = this.input.value;
        this.input.value = String(this._clamp(v));
        this._sync();
        if (!silent && before !== this.input.value) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      _onClick(e) {
        var btn = e.target.closest('button');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        var current = this.value;
        if (btn === this.minus) this._commit(current - this.step);
        else if (btn === this.plus) this._commit(current + this.step);
      }
      _onInput() {
        var parsed = parseInt(this.input.value, 10);
        this.input.value = String(this._clamp(parsed));
        this._sync();
      }
      _onKey(e) {
        if (e.key === 'ArrowUp') { e.preventDefault(); this._commit(this.value + this.step); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); this._commit(this.value - this.step); }
      }
      _sync() {
        var v = this.value;
        if (this.minus) this.minus.disabled = v <= this.min;
        if (this.plus) this.plus.disabled = v >= this.max;
        this._renderSuffix();
      }
      // "1 KT (2.500 Stk.)": cartons plus the matching number of pieces (data-pack = pieces per carton).
      _renderSuffix() {
        if (!this.suffix) return;
        var pack = parseInt(this.getAttribute('data-pack'), 10);
        var typed = parseInt(this.input.value, 10);
        var qty = isNaN(typed) ? this.min : Math.min(Math.max(typed, this.min), this.max);
        this.input.size = Math.max(String(this.input.value).length, 1);
        if (!(pack > 0)) { this.suffix.textContent = ''; return; }
        var nf = new Intl.NumberFormat(cfg.locale || undefined);
        var unit = qty === 1 ? this.getAttribute('data-carton') : (this.getAttribute('data-carton-plural') || this.getAttribute('data-carton'));
        this.suffix.textContent = (unit || '') + ' (' +
          nf.format(qty * pack) + ' ' + (this.getAttribute('data-pieces') || '') + ')';
      }
    }
    customElements.define('fd-qty', FdQty);
  }
})();
